import { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { useGlobalState } from './GlobalStateProvider';
import {
  isPostHogActive,
  getPostHogConsent,
  grantPostHogConsent,
  denyPostHogConsent,
  identifyPostHog,
} from '@/lib/posthog';

// Update to your published policy. Kept as a constant so it's easy to change.
const PRIVACY_URL = 'https://os-coach.com/privacy';

/**
 * Opt-in analytics consent banner. PostHog is initialized but captures nothing
 * until the user accepts (`opt_out_capturing_by_default: true` in the init).
 * Shown only when analytics is active AND no choice has been made yet; the
 * choice is persisted by PostHog so the banner appears once.
 */
export default function ConsentBanner() {
  const [show, setShow] = useState(false);
  const gs = useGlobalState();

  useEffect(() => {
    if (isPostHogActive() && getPostHogConsent() === 'unknown') {
      setShow(true);
    }
  }, []);

  if (!show) return null;

  const accept = () => {
    grantPostHogConsent();
    // Re-identify in case the user was already logged in before consenting
    // (their earlier identify was suppressed while opted out).
    const u = (gs as any)?.authUser;
    if (u?.id) identifyPostHog({ id: u.id, email: u.email ?? undefined });
    setShow(false);
  };

  const decline = () => {
    denyPostHogConsent();
    setShow(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Analytics consent"
      aria-live="polite"
      className="fixed inset-x-0 bottom-0 z-[100] p-4 sm:p-6"
    >
      <div className="mx-auto flex max-w-2xl flex-col gap-4 rounded-2xl border border-border bg-background/95 p-5 shadow-xl backdrop-blur sm:flex-row sm:items-center">
        <img
          src="/branding/coachos-icon.png"
          alt=""
          width={36}
          height={36}
          className="h-9 w-9 shrink-0 rounded-xl object-cover shadow-sm ring-1 ring-black/5 dark:ring-white/10"
          decoding="async"
        />
        <div className="flex-1">
          <p className="text-sm font-semibold tracking-tight text-foreground">Help us improve CoachOS</p>
          <p className="mt-1 text-sm text-muted-foreground">
            We use privacy-friendly analytics to understand usage and make CoachOS better. Client health data and
            message content are never recorded.{' '}
            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
              Learn more
            </a>
            .
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button variant="ghost" size="sm" onClick={decline}>
            Decline
          </Button>
          <Button size="sm" onClick={accept}>
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
}
